import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  getDevices, getDrivingReport, getSpeedReport, getStopsReport, getStopsDetailReport,
  getFuelReport, getDriverTimeReport, getDriverTimeWeeklyReport,
  Device, DrivingReportRow, SpeedReportRow, StopsReportRow, StopsDetailRow,
  FuelReportRow, DriverTimeRow, DriverTimeWeeklyRow,
} from '../services/api';
import Header from '../components/Layout/Header';
import RouteHistory from './RouteHistory';
import FuelReport from '../components/FuelReport/FuelReport';
import './Reports.css';

type Preset = 'yesterday' | 'week' | 'month' | 'custom';
type Tab = 'driving' | 'speed' | 'stops' | 'fuel' | 'worktime' | 'history';
type WorktimeView = 'daily' | 'weekly';

const PAGE_SIZE = 50;

const toIso = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getISOWeek = (d: Date): number => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const week1 = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};

const presetRange = (p: Preset): { from: string; to: string } => {
  const now = new Date();
  if (p === 'yesterday') {
    const y = new Date(now);
    y.setDate(now.getDate() - 1);
    return { from: toIso(y), to: toIso(y) };
  }
  if (p === 'week') {
    const day = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setDate(now.getDate() - day);
    return { from: toIso(monday), to: toIso(now) };
  }
  if (p === 'month') {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: toIso(first), to: toIso(now) };
  }
  return { from: toIso(now), to: toIso(now) };
};

const fmtDuration = (sec: number) =>
  `${Math.floor(sec / 3600).toString().padStart(2, '0')}:${Math.floor((sec % 3600) / 60).toString().padStart(2, '0')}`;

const fmtMin = (min: number) =>
  `${Math.floor(min / 60).toString().padStart(2, '0')}:${(min % 60).toString().padStart(2, '0')}`;


const remainingClass = (min: number) =>
  min > 120 ? 'remaining-green' : min >= 60 ? 'remaining-yellow' : 'remaining-red';

const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;

const downloadCsv = (lines: string[], filename: string) => {
  const csv = '﻿' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const FUEL_SOURCE_LABELS: Record<string, string> = {
  io84: 'CAN (poziom l)',
  io89: 'CAN (poziom %)',
  io81: 'CAN (przepływ)',
  brak: '—',
};

interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  label: string;
  onPrev: () => void;
  onNext: () => void;
}

const Pagination: React.FC<PaginationProps> = ({ page, totalPages, total, label, onPrev, onNext }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="page-btn" onClick={onPrev} disabled={page === 1}>Poprzednia</button>
      <span className="page-info">Strona {page} z {totalPages} (łącznie {total} {label})</span>
      <button className="page-btn" onClick={onNext} disabled={page === totalPages}>Następna</button>
    </div>
  );
};

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // ── Wspólne filtry ──
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceId, setDeviceId] = useState<string>('all');
  const [preset, setPreset] = useState<Preset>('week');
  const initialRange = presetRange('week');
  const [dateFrom, setDateFrom] = useState<string>(initialRange.from);
  const [dateTo, setDateTo] = useState<string>(initialRange.to);

  // ── Tab ──
  const [activeTab, setActiveTab] = useState<Tab>('driving');

  // ── Raport jazdy ──
  const [drivingRows, setDrivingRows] = useState<DrivingReportRow[]>([]);
  const [drivingLoading, setDrivingLoading] = useState(false);
  const [drivingError, setDrivingError] = useState<string | null>(null);
  const [drivingPage, setDrivingPage] = useState(1);

  // ── Raport prędkości ──
  const [speedRows, setSpeedRows] = useState<SpeedReportRow[]>([]);
  const [speedLoading, setSpeedLoading] = useState(false);
  const [speedError, setSpeedError] = useState<string | null>(null);
  const [speedPage, setSpeedPage] = useState(1);
  const [threshold, setThreshold] = useState<string>('');

  // ── Raport postojów (wszystkie pojazdy) ──
  const [stopsRows, setStopsRows] = useState<StopsReportRow[]>([]);
  const [stopsLoading, setStopsLoading] = useState(false);
  const [stopsError, setStopsError] = useState<string | null>(null);
  const [stopsPage, setStopsPage] = useState(1);
  const [minDuration, setMinDuration] = useState<string>('5');

  // ── Raport postojów (konkretny pojazd — STOP/PARKING z adresem) ──
  const [stopsDetailRows, setStopsDetailRows] = useState<StopsDetailRow[]>([]);
  const [stopsDetailPage, setStopsDetailPage] = useState(1);
  const [stopsTypeFilter, setStopsTypeFilter] = useState<'all' | 'stop' | 'parking'>('all');

  // ── Raport paliwa ──
  const [fuelRows, setFuelRows] = useState<FuelReportRow[]>([]);
  const [fuelLoading, setFuelLoading] = useState(false);
  const [fuelError, setFuelError] = useState<string | null>(null);
  const [fuelPage, setFuelPage] = useState(1);
  // Trigger for detailed fuel report (when specific device selected)
  const [fuelTrigger, setFuelTrigger] = useState(0);

  // ── Czas pracy - dzienny ──
  const [worktimeRows, setWorktimeRows] = useState<DriverTimeRow[]>([]);
  const [worktimeLoading, setWorktimeLoading] = useState(false);
  const [worktimeError, setWorktimeError] = useState<string | null>(null);
  const [worktimePage, setWorktimePage] = useState(1);

  // ── Czas pracy - tygodniowy ──
  const [worktimeWeeklyRows, setWorktimeWeeklyRows] = useState<DriverTimeWeeklyRow[]>([]);
  const [worktimeWeeklyLoading, setWorktimeWeeklyLoading] = useState(false);
  const [worktimeWeeklyError, setWorktimeWeeklyError] = useState<string | null>(null);
  const [worktimeView, setWorktimeView] = useState<WorktimeView>('daily');
  const _now = new Date();
  const [wtYear, setWtYear] = useState<string>(String(_now.getFullYear()));
  const [wtWeek, setWtWeek] = useState<string>(String(getISOWeek(_now)));

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    getDevices()
      .then((d) => setDevices(Array.isArray(d) ? d : []))
      .catch(() => setDevices([]));
  }, [user, navigate]);

  // Reset detailed fuel report when device changes
  useEffect(() => {
    setFuelTrigger(0);
  }, [deviceId]);

  const applyPreset = (p: Preset) => {
    setPreset(p);
    if (p !== 'custom') {
      const r = presetRange(p);
      setDateFrom(r.from);
      setDateTo(r.to);
    }
  };

  const generateDriving = async () => {
    setDrivingLoading(true); setDrivingError(null); setDrivingPage(1);
    try {
      const did = deviceId === 'all' ? 'all' : Number(deviceId);
      setDrivingRows(await getDrivingReport(did as number | 'all', dateFrom, dateTo));
    } catch (e: any) {
      setDrivingError(e?.response?.data?.detail || 'Nie udało się pobrać raportu');
      setDrivingRows([]);
    } finally { setDrivingLoading(false); }
  };

  const generateSpeed = async () => {
    setSpeedLoading(true); setSpeedError(null); setSpeedPage(1);
    try {
      const did = deviceId === 'all' ? 'all' : Number(deviceId);
      const thr = threshold !== '' ? Number(threshold) : undefined;
      setSpeedRows(await getSpeedReport(did as number | 'all', dateFrom, dateTo, thr));
    } catch (e: any) {
      setSpeedError(e?.response?.data?.detail || 'Nie udało się pobrać raportu');
      setSpeedRows([]);
    } finally { setSpeedLoading(false); }
  };

  const generateStops = async () => {
    setStopsLoading(true); setStopsError(null); setStopsPage(1); setStopsDetailPage(1);
    try {
      if (deviceId === 'all') {
        const min = minDuration !== '' ? Number(minDuration) : 5;
        setStopsRows(await getStopsReport('all', dateFrom, dateTo, min));
        setStopsDetailRows([]);
      } else {
        setStopsDetailRows(await getStopsDetailReport(Number(deviceId), dateFrom, dateTo));
        setStopsRows([]);
      }
    } catch (e: any) {
      setStopsError(e?.response?.data?.detail || 'Nie udało się pobrać raportu');
      setStopsRows([]); setStopsDetailRows([]);
    } finally { setStopsLoading(false); }
  };

  const generateFuel = async () => {
    if (deviceId !== 'all') {
      // Detailed per-vehicle report — FuelReport component handles its own loading
      setFuelTrigger((t) => t + 1);
      return;
    }
    setFuelLoading(true); setFuelError(null); setFuelPage(1);
    try {
      setFuelRows(await getFuelReport('all', dateFrom, dateTo));
    } catch (e: any) {
      setFuelError(e?.response?.data?.detail || 'Nie udało się pobrać raportu');
      setFuelRows([]);
    } finally { setFuelLoading(false); }
  };

  const generateWorktime = async () => {
    setWorktimeLoading(true); setWorktimeError(null); setWorktimePage(1);
    try {
      const did = deviceId === 'all' ? 'all' : Number(deviceId);
      const rows = await getDriverTimeReport(did as number | 'all', dateFrom, dateTo);
      setWorktimeRows(rows);
    } catch (e: any) {
      setWorktimeError(e?.response?.data?.detail || 'Nie udało się pobrać raportu');
      setWorktimeRows([]);
    } finally { setWorktimeLoading(false); }
  };

  const generateWorktimeWeekly = async () => {
    setWorktimeWeeklyLoading(true); setWorktimeWeeklyError(null);
    try {
      const did = deviceId === 'all' ? 'all' : Number(deviceId);
      const rows = await getDriverTimeWeeklyReport(did as number | 'all', Number(wtYear), Number(wtWeek));
      setWorktimeWeeklyRows(rows);
    } catch (e: any) {
      setWorktimeWeeklyError(e?.response?.data?.detail || 'Nie udało się pobrać raportu');
      setWorktimeWeeklyRows([]);
    } finally { setWorktimeWeeklyLoading(false); }
  };

  const isWorktimeWeekly = activeTab === 'worktime' && worktimeView === 'weekly';

  const generate = activeTab === 'driving' ? generateDriving
    : activeTab === 'speed'    ? generateSpeed
    : activeTab === 'stops'    ? generateStops
    : activeTab === 'fuel'     ? generateFuel
    : isWorktimeWeekly         ? generateWorktimeWeekly
    : generateWorktime;

  const loading = activeTab === 'driving' ? drivingLoading
    : activeTab === 'speed'    ? speedLoading
    : activeTab === 'stops'    ? stopsLoading
    : activeTab === 'fuel'     ? (deviceId === 'all' ? fuelLoading : false)
    : isWorktimeWeekly         ? worktimeWeeklyLoading
    : worktimeLoading;

  // ── Jazda ──
  const visibleDriving = useMemo(
    () => drivingRows.filter((r) => !(r.drive_start === null && r.driving_seconds === 0)),
    [drivingRows],
  );
  const drivingTotalPages = Math.max(1, Math.ceil(visibleDriving.length / PAGE_SIZE));
  const drivingPageRows   = visibleDriving.slice((drivingPage - 1) * PAGE_SIZE, drivingPage * PAGE_SIZE);
  const drivingTotals = useMemo(() => {
    let seconds = 0; let km = 0;
    for (const r of visibleDriving) { seconds += r.driving_seconds; km += r.distance_km; }
    return { seconds, km };
  }, [visibleDriving]);

  // ── Prędkość ──
  const speedTotalPages = Math.max(1, Math.ceil(speedRows.length / PAGE_SIZE));
  const speedPageRows   = speedRows.slice((speedPage - 1) * PAGE_SIZE, speedPage * PAGE_SIZE);

  // ── Postoje (wszystkie pojazdy) ──
  const stopsTotalPages = Math.max(1, Math.ceil(stopsRows.length / PAGE_SIZE));
  const stopsPageRows   = stopsRows.slice((stopsPage - 1) * PAGE_SIZE, stopsPage * PAGE_SIZE);
  const stopsTotals = useMemo(() => {
    let minutes = 0;
    for (const r of stopsRows) { minutes += r.duration_minutes; }
    return { count: stopsRows.length, minutes };
  }, [stopsRows]);

  // ── Postoje szczegółowe (konkretny pojazd) ──
  const filteredDetailRows = useMemo(() => {
    if (stopsTypeFilter === 'all') return stopsDetailRows;
    return stopsDetailRows.filter(r => r.event_type === stopsTypeFilter);
  }, [stopsDetailRows, stopsTypeFilter]);

  const detailTotalPages = Math.max(1, Math.ceil(filteredDetailRows.length / PAGE_SIZE));
  const detailPageRows   = filteredDetailRows.slice((stopsDetailPage - 1) * PAGE_SIZE, stopsDetailPage * PAGE_SIZE);

  const detailStats = useMemo(() => {
    let stopMin = 0; let parkMin = 0; let stopCnt = 0; let parkCnt = 0;
    for (const r of stopsDetailRows) {
      if (r.event_type === 'stop')    { stopMin += r.duration_minutes; stopCnt++; }
      else                            { parkMin += r.duration_minutes; parkCnt++; }
    }
    return { stopMin, parkMin, stopCnt, parkCnt };
  }, [stopsDetailRows]);

  // ── Paliwo ──
  const fuelTotalPages = Math.max(1, Math.ceil(fuelRows.length / PAGE_SIZE));
  const fuelPageRows   = fuelRows.slice((fuelPage - 1) * PAGE_SIZE, fuelPage * PAGE_SIZE);
  const fuelTotals = useMemo(() => {
    let liters = 0; let km = 0;
    for (const r of fuelRows) { liters += r.fuel_used_liters; km += r.distance_km; }
    return {
      liters: Math.round(liters * 10) / 10,
      km:     Math.round(km * 10) / 10,
      avg:    km > 0 ? Math.round(liters / km * 1000) / 10 : null,
    };
  }, [fuelRows]);

  // ── Czas pracy ──
  const worktimeTotalPages = Math.max(1, Math.ceil(worktimeRows.length / PAGE_SIZE));
  const worktimePageRows   = worktimeRows.slice((worktimePage - 1) * PAGE_SIZE, worktimePage * PAGE_SIZE);

  // Sumy jazdy per tydzień (do obliczenia pozostałego czasu tygodniowego w widoku dziennym)
  const worktimeWeekTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const r of worktimeRows) {
      const year = r.date.substring(0, 4);
      const key = `${r.device_id}-${year}-${r.week_number}`;
      totals[key] = (totals[key] || 0) + r.drive_time_minutes;
    }
    return totals;
  }, [worktimeRows]);

  // ── CSV jazdy ──
  const exportDrivingCsv = () => {
    const header = ['Pojazd', 'Data', 'Początek', 'Koniec', 'Czas jazdy', 'Dystans (km)'];
    const lines = [header.map(escape).join(',')];
    for (const r of visibleDriving) {
      lines.push([
        escape(r.device_name), escape(r.date),
        escape(r.drive_start ?? '—'), escape(r.drive_end ?? '—'),
        escape(r.driving_time), escape(r.distance_km.toFixed(1)),
      ].join(','));
    }
    lines.push([escape('SUMA'), escape(''), escape(''), escape(''),
      escape(fmtDuration(drivingTotals.seconds)), escape(drivingTotals.km.toFixed(1))].join(','));
    downloadCsv(lines, `raport-jazdy_${dateFrom}_${dateTo}.csv`);
  };

  // ── CSV prędkości ──
  const exportSpeedCsv = () => {
    const header = ['Pojazd', 'Data i godzina', 'Prędkość (km/h)', 'Limit (km/h)', 'Przekroczenie (km/h)'];
    const lines = [header.map(escape).join(',')];
    for (const r of speedRows) {
      lines.push([
        escape(r.device_name), escape(r.datetime),
        escape(r.speed_kmh.toFixed(1)), escape(String(r.limit_kmh)), escape(r.excess_kmh.toFixed(1)),
      ].join(','));
    }
    downloadCsv(lines, `raport-predkosci_${dateFrom}_${dateTo}.csv`);
  };

  // ── CSV postojów ──
  const exportStopsCsv = () => {
    const header = ['Pojazd', 'Data', 'Początek', 'Koniec', 'Czas (min)', 'Szerokość', 'Długość'];
    const lines = [header.map(escape).join(',')];
    for (const r of stopsRows) {
      lines.push([
        escape(r.device_name), escape(r.date), escape(r.start_time), escape(r.end_time),
        escape(String(r.duration_minutes)), escape(String(r.latitude)), escape(String(r.longitude)),
      ].join(','));
    }
    lines.push([escape('SUMA'), escape(''), escape(''), escape(''),
      escape(String(stopsTotals.minutes)), escape(''), escape('')].join(','));
    downloadCsv(lines, `raport-postojow_${dateFrom}_${dateTo}.csv`);
  };

  // ── CSV postojów szczegółowych ──
  const exportStopsDetailCsv = () => {
    const header = ['Typ', 'Data', 'Czas start', 'Czas koniec', 'Czas (min)', 'Adres', 'Lat', 'Lon'];
    const lines = [header.map(escape).join(',')];
    for (const r of filteredDetailRows) {
      lines.push([
        escape(r.event_type === 'stop' ? 'STOP' : 'Parkowanie'),
        escape(r.start_time.substring(0, 10)),
        escape(r.start_time.substring(11, 16)),
        escape(r.end_time.substring(11, 16)),
        escape(String(r.duration_minutes)),
        escape(r.address || ''),
        escape(String(r.latitude)),
        escape(String(r.longitude)),
      ].join(','));
    }
    downloadCsv(lines, `raport-postojow_${dateFrom}_${dateTo}.csv`);
  };

  // ── CSV paliwa ──
  const exportFuelCsv = () => {
    const header = ['Pojazd', 'Data', 'Zużycie (l)', 'Dystans (km)', 'Śr. spalanie (l/100km)', 'Źródło danych'];
    const lines = [header.map(escape).join(',')];
    for (const r of fuelRows) {
      lines.push([
        escape(r.device_name), escape(r.date),
        escape(r.fuel_used_liters.toFixed(1)), escape(r.distance_km.toFixed(1)),
        escape(r.avg_consumption != null ? r.avg_consumption.toFixed(1) : '—'),
        escape(FUEL_SOURCE_LABELS[r.fuel_source_used] ?? r.fuel_source_used),
      ].join(','));
    }
    lines.push([escape('SUMA'), escape(''), escape(fuelTotals.liters.toFixed(1)),
      escape(fuelTotals.km.toFixed(1)),
      escape(fuelTotals.avg != null ? fuelTotals.avg.toFixed(1) : '—'), escape('')].join(','));
    downloadCsv(lines, `raport-paliwa_${dateFrom}_${dateTo}.csv`);
  };

  // ── CSV czasu pracy dzienny ──
  const exportWorktimeCsv = () => {
    const header = ['Pojazd', 'Data', 'Tydzień', 'Czas jazdy', 'Czas pracy', 'Przerwy', 'Pozostało dziś', 'Pozostało w tygodniu'];
    const lines = [header.map(escape).join(',')];
    for (const r of worktimeRows) {
      const year = r.date.substring(0, 4);
      const weekKey = `${r.device_id}-${year}-${r.week_number}`;
      const weekRemaining = Math.max(0, 3360 - (worktimeWeekTotals[weekKey] || 0));
      lines.push([
        escape(r.device_name), escape(r.date), escape(String(r.week_number)),
        escape(fmtMin(r.drive_time_minutes)), escape(fmtMin(r.total_work_minutes)),
        escape(fmtMin(r.break_time_minutes)), escape(fmtMin(r.day_remaining_minutes)),
        escape(fmtMin(weekRemaining)),
      ].join(','));
    }
    downloadCsv(lines, `raport-czasu-pracy_${dateFrom}_${dateTo}.csv`);
  };

  // ── CSV czasu pracy tygodniowy ──
  const exportWorktimeWeeklyCsv = () => {
    const header = ['Pojazd', 'Rok', 'Tydzień', 'Łączna jazda', 'Łączny czas pracy', 'Pozostało (tygodniowe)'];
    const lines = [header.map(escape).join(',')];
    for (const r of worktimeWeeklyRows) {
      lines.push([
        escape(r.device_name), escape(String(r.year)), escape(String(r.week_number)),
        escape(fmtMin(r.total_drive_minutes)), escape(fmtMin(r.total_work_minutes)),
        escape(fmtMin(r.week_remaining_minutes)),
      ].join(','));
    }
    downloadCsv(lines, `raport-czasu-pracy-tygodniowy_${wtYear}_W${wtWeek}.csv`);
  };

  const canExport = activeTab === 'driving'  ? visibleDriving.length > 0
    : activeTab === 'speed'                  ? speedRows.length > 0
    : activeTab === 'stops'                  ? (deviceId !== 'all' ? filteredDetailRows.length > 0 : stopsRows.length > 0)
    : activeTab === 'fuel'                   ? (deviceId === 'all' && fuelRows.length > 0)
    : isWorktimeWeekly                       ? worktimeWeeklyRows.length > 0
    : worktimeRows.length > 0;

  const onExport = activeTab === 'driving'   ? exportDrivingCsv
    : activeTab === 'speed'                  ? exportSpeedCsv
    : activeTab === 'stops'                  ? (deviceId !== 'all' ? exportStopsDetailCsv : exportStopsCsv)
    : activeTab === 'fuel'                   ? exportFuelCsv
    : isWorktimeWeekly                       ? exportWorktimeWeeklyCsv
    : exportWorktimeCsv;

  return (
    <div className="reports-layout">
      <Header />
      <div className="reports-body">
        <div className="reports-container">

          {/* ── Przełącznik zakładek ── */}
          <div className="tab-switcher">
            <button className={`tab-btn ${activeTab === 'driving'  ? 'active' : ''}`} onClick={() => setActiveTab('driving')}>Raport jazdy</button>
            <button className={`tab-btn ${activeTab === 'speed'    ? 'active' : ''}`} onClick={() => setActiveTab('speed')}>Przekroczenia prędkości</button>
            <button className={`tab-btn ${activeTab === 'stops'    ? 'active' : ''}`} onClick={() => setActiveTab('stops')}>Postoje</button>
            <button className={`tab-btn ${activeTab === 'fuel'     ? 'active' : ''}`} onClick={() => setActiveTab('fuel')}>Paliwo</button>
            <button className={`tab-btn ${activeTab === 'worktime' ? 'active' : ''}`} onClick={() => setActiveTab('worktime')}>Czas pracy</button>
            <button className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>Historia tras</button>
          </div>

          {/* ── Filtry (ukryte dla historii tras — ma własny UI) ── */}
          {activeTab !== 'history' && (
          <div className="filters">

            {/* Przełącznik dzienny/tygodniowy – tylko dla zakładki Czas pracy */}
            {activeTab === 'worktime' && (
              <div className="filter-group">
                <label className="filter-label">Widok</label>
                <div className="preset-buttons">
                  <button className={`preset-btn ${worktimeView === 'daily'  ? 'active' : ''}`} onClick={() => setWorktimeView('daily')}>Dzienny</button>
                  <button className={`preset-btn ${worktimeView === 'weekly' ? 'active' : ''}`} onClick={() => setWorktimeView('weekly')}>Tygodniowy</button>
                </div>
              </div>
            )}

            {/* Zakres dat – wszystkie zakładki poza Czas pracy tygodniowy */}
            {!isWorktimeWeekly && (
              <>
                <div className="filter-group">
                  <label className="filter-label">Zakres</label>
                  <div className="preset-buttons">
                    {(['yesterday', 'week', 'month', 'custom'] as Preset[]).map((p) => (
                      <button key={p} className={`preset-btn ${preset === p ? 'active' : ''}`} onClick={() => applyPreset(p)}>
                        {{ yesterday: 'Wczoraj', week: 'Bieżący tydzień', month: 'Bieżący miesiąc', custom: 'Własny' }[p]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="filter-group">
                  <label className="filter-label">Od</label>
                  <input type="date" className="filter-input" value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPreset('custom'); }} />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Do</label>
                  <input type="date" className="filter-input" value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPreset('custom'); }} />
                </div>
              </>
            )}

            {/* Rok + Tydzień – tylko dla Czas pracy tygodniowy */}
            {isWorktimeWeekly && (
              <>
                <div className="filter-group">
                  <label className="filter-label">Rok</label>
                  <input type="number" className="filter-input filter-input--narrow"
                    value={wtYear} min={2020} max={2099}
                    onChange={(e) => setWtYear(e.target.value)} />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Tydzień (ISO)</label>
                  <input type="number" className="filter-input filter-input--narrow"
                    value={wtWeek} min={1} max={53}
                    onChange={(e) => setWtWeek(e.target.value)} />
                </div>
              </>
            )}

            <div className="filter-group">
              <label className="filter-label">Pojazd</label>
              <select className="filter-input" value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
                <option value="all">Wszystkie pojazdy</option>
                {devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            {activeTab === 'speed' && (
              <div className="filter-group">
                <label className="filter-label">Próg (km/h)</label>
                <input type="number" className="filter-input filter-input--narrow"
                  value={threshold} min={1} max={300} placeholder="domyślny dla pojazdu"
                  onChange={(e) => setThreshold(e.target.value)} />
              </div>
            )}

            {activeTab === 'stops' && deviceId === 'all' && (
              <div className="filter-group">
                <label className="filter-label">Min. czas postoju (min)</label>
                <input type="number" className="filter-input filter-input--narrow"
                  value={minDuration} min={1} max={999}
                  onChange={(e) => setMinDuration(e.target.value)} />
              </div>
            )}
            {activeTab === 'stops' && deviceId !== 'all' && (
              <div className="filter-group">
                <label className="filter-label">Maks. zakres</label>
                <span className="filter-note">31 dni · STOP/PARKING + adres</span>
              </div>
            )}

            <div className="filter-actions">
              <button className="btn-primary" onClick={generate} disabled={loading}>
                {loading ? 'Generuję…' : 'Generuj raport'}
              </button>
              {!(activeTab === 'fuel' && deviceId !== 'all') && (
                <button className="btn-secondary" onClick={onExport} disabled={!canExport}
                  title={!canExport ? 'Brak danych do eksportu' : 'Pobierz CSV'}>
                  Pobierz CSV
                </button>
              )}
            </div>
          </div>
          )}

          {activeTab === 'driving'  && drivingError        && <div className="reports-error">{drivingError}</div>}
          {activeTab === 'speed'    && speedError          && <div className="reports-error">{speedError}</div>}
          {activeTab === 'stops'    && stopsError          && <div className="reports-error">{stopsError}</div>}
          {activeTab === 'fuel'     && fuelError           && <div className="reports-error">{fuelError}</div>}
          {activeTab === 'worktime' && !isWorktimeWeekly  && worktimeError       && <div className="reports-error">{worktimeError}</div>}
          {activeTab === 'worktime' && isWorktimeWeekly   && worktimeWeeklyError && <div className="reports-error">{worktimeWeeklyError}</div>}

          {/* ── Tabela jazdy ── */}
          {activeTab === 'driving' && (
            <>
              <div className="table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Pojazd</th><th>Data</th><th>Początek</th>
                      <th>Koniec</th><th>Czas jazdy</th><th className="num">Dystans (km)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drivingPageRows.length === 0 && !drivingLoading && (
                      <tr><td colSpan={6} className="empty-row">
                        {drivingRows.length === 0 ? 'Kliknij "Generuj raport"' : 'Brak dni z jazdą w wybranym zakresie'}
                      </td></tr>
                    )}
                    {drivingPageRows.map((r, idx) => (
                      <tr key={`${r.device_id}-${r.date}-${idx}`}>
                        <td>{r.device_name}</td><td>{r.date}</td>
                        <td>{r.drive_start ?? '—'}</td><td>{r.drive_end ?? '—'}</td>
                        <td>{r.driving_time}</td>
                        <td className="num">{r.distance_km.toFixed(1)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {visibleDriving.length > 0 && (
                    <tfoot>
                      <tr className="totals-row">
                        <td colSpan={4}>SUMA</td>
                        <td>{fmtDuration(drivingTotals.seconds)}</td>
                        <td className="num">{drivingTotals.km.toFixed(1)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <Pagination page={drivingPage} totalPages={drivingTotalPages} total={visibleDriving.length} label="dni"
                onPrev={() => setDrivingPage((p) => p - 1)} onNext={() => setDrivingPage((p) => p + 1)} />
            </>
          )}

          {/* ── Tabela przekroczeń prędkości ── */}
          {activeTab === 'speed' && (
            <>
              <div className="table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Pojazd</th><th>Data i godzina</th>
                      <th className="num">Prędkość (km/h)</th>
                      <th className="num">Limit (km/h)</th>
                      <th className="num excess-col">Przekroczenie (km/h)</th>
                      <th>Lokalizacja</th>
                    </tr>
                  </thead>
                  <tbody>
                    {speedPageRows.length === 0 && !speedLoading && (
                      <tr><td colSpan={6} className="empty-row">
                        {speedRows.length === 0 ? 'Kliknij "Generuj raport"' : 'Brak przekroczeń w wybranym zakresie'}
                      </td></tr>
                    )}
                    {speedPageRows.map((r, idx) => (
                      <tr key={`${r.device_id}-${r.datetime}-${idx}`} className="speed-row">
                        <td>{r.device_name}</td><td>{r.datetime}</td>
                        <td className="num">{r.speed_kmh.toFixed(1)}</td>
                        <td className="num">{r.limit_kmh}</td>
                        <td className="num excess-col">{r.excess_kmh.toFixed(1)}</td>
                        <td>
                          <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                            target="_blank" rel="noreferrer" className="map-link">📍 Pokaż</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {speedRows.length > 0 && (
                    <tfoot>
                      <tr className="totals-row">
                        <td colSpan={5}>Łącznie przekroczeń</td>
                        <td className="num">{speedRows.length}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <Pagination page={speedPage} totalPages={speedTotalPages} total={speedRows.length} label="przekroczeń"
                onPrev={() => setSpeedPage((p) => p - 1)} onNext={() => setSpeedPage((p) => p + 1)} />
            </>
          )}

          {/* ── Postoje — tryb "wszystkie pojazdy" ── */}
          {activeTab === 'stops' && deviceId === 'all' && (
            <>
              <div className="table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Pojazd</th><th>Data</th><th>Początek</th>
                      <th>Koniec</th><th className="num">Czas (min)</th><th>Lokalizacja</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stopsPageRows.length === 0 && !stopsLoading && (
                      <tr><td colSpan={6} className="empty-row">
                        {stopsRows.length === 0 ? 'Kliknij "Generuj raport"' : 'Brak postojów w wybranym zakresie'}
                      </td></tr>
                    )}
                    {stopsPageRows.map((r, idx) => (
                      <tr key={`${r.device_id}-${r.date}-${r.start_time}-${idx}`}>
                        <td>{r.device_name}</td><td>{r.date}</td>
                        <td>{r.start_time}</td><td>{r.end_time}</td>
                        <td className="num">{r.duration_minutes}</td>
                        <td>
                          <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                            target="_blank" rel="noreferrer" className="map-link">📍 Pokaż</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {stopsRows.length > 0 && (
                    <tfoot>
                      <tr className="totals-row">
                        <td colSpan={4}>Łącznie postojów: {stopsTotals.count}</td>
                        <td className="num">{stopsTotals.minutes}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <Pagination page={stopsPage} totalPages={stopsTotalPages} total={stopsRows.length} label="postojów"
                onPrev={() => setStopsPage((p) => p - 1)} onNext={() => setStopsPage((p) => p + 1)} />
            </>
          )}

          {/* ── Postoje — tryb konkretnego pojazdu (STOP/PARKING + adres) ── */}
          {activeTab === 'stops' && deviceId !== 'all' && (
            <>
              {/* Podsumowanie */}
              {stopsDetailRows.length > 0 && (
                <div className="stops-summary">
                  <div className="stops-summary-item">
                    <span className="stops-summary-label">Postoje STOP</span>
                    <span className="stops-summary-value stops-summary-stop">{detailStats.stopCnt}</span>
                  </div>
                  <div className="stops-summary-item">
                    <span className="stops-summary-label">Czas STOP</span>
                    <span className="stops-summary-value stops-summary-stop">{fmtMin(detailStats.stopMin)}</span>
                  </div>
                  <div className="stops-summary-sep" />
                  <div className="stops-summary-item">
                    <span className="stops-summary-label">Parkowania</span>
                    <span className="stops-summary-value stops-summary-park">{detailStats.parkCnt}</span>
                  </div>
                  <div className="stops-summary-item">
                    <span className="stops-summary-label">Czas parkowania</span>
                    <span className="stops-summary-value stops-summary-park">{fmtMin(detailStats.parkMin)}</span>
                  </div>
                  <div className="stops-summary-sep" />
                  <div className="stops-summary-item">
                    <span className="stops-summary-label">Łącznie</span>
                    <span className="stops-summary-value">{fmtMin(detailStats.stopMin + detailStats.parkMin)}</span>
                  </div>
                </div>
              )}

              {/* Filtr typu */}
              {stopsDetailRows.length > 0 && (
                <div className="stops-type-bar">
                  {(['all', 'stop', 'parking'] as const).map((f) => (
                    <button key={f}
                      className={`stops-type-btn ${stopsTypeFilter === f ? 'active' : ''}`}
                      onClick={() => { setStopsTypeFilter(f); setStopsDetailPage(1); }}>
                      {f === 'all' && 'Wszystkie'}
                      {f === 'stop' && (
                        <><svg width="14" height="14" viewBox="0 0 28 28" style={{display:'inline',verticalAlign:'middle',marginRight:4}}>
                          <polygon points="10,2 18,2 26,10 26,18 18,26 10,26 2,18 2,10" fill="#dc2626" stroke="white" strokeWidth="2" strokeLinejoin="round"/>
                          <text x="14" y="17.5" textAnchor="middle" fill="white" fontSize="6.5" fontWeight="bold" fontFamily="Arial,sans-serif" letterSpacing="-0.3">STOP</text>
                        </svg>STOP</>
                      )}
                      {f === 'parking' && (
                        <><svg width="14" height="14" viewBox="0 0 26 26" style={{display:'inline',verticalAlign:'middle',marginRight:4}}>
                          <circle cx="13" cy="13" r="12" fill="#2b6cc4" stroke="white" strokeWidth="2"/>
                          <text x="13" y="18" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold" fontFamily="Arial,sans-serif">P</text>
                        </svg>Parkowanie</>
                      )}
                    </button>
                  ))}
                  <span className="stops-type-count">{filteredDetailRows.length} zdarzeń</span>
                </div>
              )}

              {/* Tabela */}
              <div className="table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Typ</th><th>Data</th><th>Czas start</th>
                      <th>Czas koniec</th><th className="num">Czas trwania</th><th>Adres</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailPageRows.length === 0 && !stopsLoading && (
                      <tr><td colSpan={6} className="empty-row">
                        {stopsDetailRows.length === 0
                          ? 'Wybierz zakres dat i kliknij "Generuj raport" (maks. 31 dni)'
                          : 'Brak zdarzeń dla wybranego filtra'}
                      </td></tr>
                    )}
                    {detailPageRows.map((r, idx) => (
                      <tr key={`${r.event_type}-${r.start_time}-${idx}`}>
                        <td className="stops-type-cell">
                          {r.event_type === 'stop' ? (
                            <svg width="22" height="22" viewBox="0 0 28 28" aria-label="STOP">
                              <polygon points="10,2 18,2 26,10 26,18 18,26 10,26 2,18 2,10" fill="#dc2626" stroke="white" strokeWidth="2" strokeLinejoin="round"/>
                              <text x="14" y="17.5" textAnchor="middle" fill="white" fontSize="6.5" fontWeight="bold" fontFamily="Arial,sans-serif" letterSpacing="-0.3">STOP</text>
                            </svg>
                          ) : (
                            <svg width="22" height="22" viewBox="0 0 26 26" aria-label="Parkowanie">
                              <circle cx="13" cy="13" r="12" fill="#2b6cc4" stroke="white" strokeWidth="2"/>
                              <text x="13" y="18" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold" fontFamily="Arial,sans-serif">P</text>
                            </svg>
                          )}
                        </td>
                        <td>{r.start_time.substring(0, 10)}</td>
                        <td>{r.start_time.substring(11, 16)}</td>
                        <td>{r.end_time.substring(11, 16)}</td>
                        <td className="num">{fmtMin(r.duration_minutes)}</td>
                        <td className="stops-address-cell">
                          <span className="stops-address-text" title={r.address}>{r.address || '—'}</span>
                          <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                            target="_blank" rel="noreferrer" className="map-link stops-map-link">📍</a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={stopsDetailPage} totalPages={detailTotalPages} total={filteredDetailRows.length} label="zdarzeń"
                onPrev={() => setStopsDetailPage((p) => p - 1)} onNext={() => setStopsDetailPage((p) => p + 1)} />
            </>
          )}

          {/* ── Raport paliwa (szczegółowy — konkretny pojazd) ── */}
          {activeTab === 'fuel' && deviceId !== 'all' && (
            fuelTrigger === 0
              ? (
                <div className="table-wrapper">
                  <table className="reports-table">
                    <tbody>
                      <tr><td className="empty-row">Wybierz zakres dat i kliknij "Generuj raport"</td></tr>
                    </tbody>
                  </table>
                </div>
              )
              : (
                <FuelReport
                  key={fuelTrigger}
                  deviceId={Number(deviceId)}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  deviceName={devices.find((d) => String(d.id) === deviceId)?.name}
                />
              )
          )}

          {/* ── Tabela paliwa (zbiorcza — wszystkie pojazdy) ── */}
          {activeTab === 'fuel' && deviceId === 'all' && (
            <>
              <div className="table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Pojazd</th><th>Data</th>
                      <th className="num">Zużycie (l)</th>
                      <th className="num">Dystans (km)</th>
                      <th className="num">Śr. spalanie (l/100km)</th>
                      <th>Źródło danych</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fuelPageRows.length === 0 && !fuelLoading && (
                      <tr><td colSpan={6} className="empty-row">
                        {fuelRows.length === 0
                          ? 'Kliknij "Generuj raport"'
                          : 'Brak danych paliwa dla wybranego okresu. Wymagane urządzenie z obsługą CAN.'}
                      </td></tr>
                    )}
                    {fuelPageRows.map((r, idx) => (
                      <tr key={`${r.device_id}-${r.date}-${idx}`}>
                        <td>{r.device_name}</td><td>{r.date}</td>
                        <td className="num">{r.fuel_used_liters.toFixed(1)}</td>
                        <td className="num">{r.distance_km.toFixed(1)}</td>
                        <td className="num">{r.avg_consumption != null ? r.avg_consumption.toFixed(1) : '—'}</td>
                        <td className="fuel-source-cell">{FUEL_SOURCE_LABELS[r.fuel_source_used] ?? r.fuel_source_used}</td>
                      </tr>
                    ))}
                  </tbody>
                  {fuelRows.length > 0 && (
                    <tfoot>
                      <tr className="totals-row">
                        <td colSpan={2}>SUMA</td>
                        <td className="num">{fuelTotals.liters.toFixed(1)}</td>
                        <td className="num">{fuelTotals.km.toFixed(1)}</td>
                        <td className="num">{fuelTotals.avg != null ? fuelTotals.avg.toFixed(1) : '—'}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              <Pagination page={fuelPage} totalPages={fuelTotalPages} total={fuelRows.length} label="wierszy"
                onPrev={() => setFuelPage((p) => p - 1)} onNext={() => setFuelPage((p) => p + 1)} />
            </>
          )}

          {/* ── Informacja o szacunkowości danych ── */}
          {activeTab === 'worktime' && (
            <p style={{ fontSize: '11px', color: '#9aa0ad', fontStyle: 'italic', margin: '0 0 8px' }}>
              ⚠️ Dane szacunkowe obliczone na podstawie GPS i stacyjki — to nie są dane z tachografu cyfrowego.
            </p>
          )}

          {/* ── Czas pracy – widok dzienny ── */}
          {activeTab === 'worktime' && worktimeView === 'daily' && (
            <>
              <div className="table-wrapper">
                <table className="reports-table">
                  <thead>
                    <tr>
                      <th>Pojazd</th>
                      <th>Data</th>
                      <th className="num">Tydzień</th>
                      <th>Czas jazdy</th>
                      <th>Czas pracy</th>
                      <th>Przerwy</th>
                      <th className="num">Pozostało dziś</th>
                      <th className="num">Pozostało w tyg.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {worktimePageRows.length === 0 && !worktimeLoading && (
                      <tr><td colSpan={8} className="empty-row">
                        {worktimeRows.length === 0
                          ? 'Kliknij "Generuj raport"'
                          : 'Brak danych o czasie pracy w wybranym zakresie'}
                      </td></tr>
                    )}
                    {worktimePageRows.map((r, idx) => {
                      const year = r.date.substring(0, 4);
                      const weekKey = `${r.device_id}-${year}-${r.week_number}`;
                      const weekRemaining = Math.max(0, 3360 - (worktimeWeekTotals[weekKey] || 0));
                      return (
                        <tr key={`${r.device_id}-${r.date}-${idx}`}>
                          <td>{r.device_name}</td>
                          <td>{r.date}</td>
                          <td className="num">{r.week_number}</td>
                          <td>{fmtMin(r.drive_time_minutes)}</td>
                          <td>{fmtMin(r.total_work_minutes)}</td>
                          <td>{fmtMin(r.break_time_minutes)}</td>
                          <td className={`num ${remainingClass(r.day_remaining_minutes)}`}>
                            {fmtMin(r.day_remaining_minutes)}
                          </td>
                          <td className={`num ${remainingClass(weekRemaining)}`}>
                            {fmtMin(weekRemaining)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pagination page={worktimePage} totalPages={worktimeTotalPages} total={worktimeRows.length} label="dni"
                onPrev={() => setWorktimePage((p) => p - 1)} onNext={() => setWorktimePage((p) => p + 1)} />
            </>
          )}

          {/* ── Czas pracy – widok tygodniowy ── */}
          {activeTab === 'worktime' && worktimeView === 'weekly' && (
            <div className="table-wrapper">
              <table className="reports-table">
                <thead>
                  <tr>
                    <th>Pojazd</th>
                    <th className="num">Rok</th>
                    <th className="num">Tydzień</th>
                    <th>Łączna jazda</th>
                    <th>Łączny czas pracy</th>
                    <th className="num">Pozostało (tygodniowe)</th>
                  </tr>
                </thead>
                <tbody>
                  {worktimeWeeklyRows.length === 0 && !worktimeWeeklyLoading && (
                    <tr><td colSpan={6} className="empty-row">Kliknij "Generuj raport"</td></tr>
                  )}
                  {worktimeWeeklyRows.map((r, idx) => (
                    <tr key={`${r.device_id}-${r.week_number}-${idx}`}>
                      <td>{r.device_name}</td>
                      <td className="num">{r.year}</td>
                      <td className="num">{r.week_number}</td>
                      <td>{fmtMin(r.total_drive_minutes)}</td>
                      <td>{fmtMin(r.total_work_minutes)}</td>
                      <td className={`num ${remainingClass(r.week_remaining_minutes)}`}>
                        {fmtMin(r.week_remaining_minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Historia tras ── */}
          {activeTab === 'history' && <RouteHistory />}

        </div>
      </div>
    </div>
  );
};

export default Reports;
